import {
  CardFragment,
  CardType,
  ColorFragment,
  DetailedLayoutFragment,
  FontFragment,
  TextAlignment,
} from 'src/graphql'

import {
  EditorElement,
  EditorFullBleed,
  EditorImage,
  EditorPanel,
} from 'src/editor/EditorCard'
import {
  Card,
  Element,
  FullBleed,
  Panel,
} from '@sendoutcards/editor/dist/types/card'
import { Color, Layout } from '@sendoutcards/editor/dist/types/primary'

export const isInsideRightPanel = (
  cardType: CardType,
  fullBleedLocation: number,
  panelLocation: number,
) => {
  switch (cardType) {
    case CardType.POSTCARD:
    case CardType.FLATCARD:
      return fullBleedLocation === 1 && panelLocation === 0
    case CardType.THREE_PANEL:
      return fullBleedLocation === 2 && panelLocation === 0
    case CardType.TWO_PANEL:
    case CardType.TWO_PANEL_BIG:
    default:
      return fullBleedLocation === 1 && panelLocation === 1
  }
}

export const updateFullBleed = (
  fullBleeds: EditorFullBleed[],
  fullBleedIndex: number,
  update: (fullBleed: EditorFullBleed) => EditorFullBleed,
) =>
  fullBleeds.map((fullBleed, index) =>
    index === fullBleedIndex ? update(fullBleed) : fullBleed,
  )

export const updatePanel = (
  fullBleeds: EditorFullBleed[],
  fullBleedIndex: number,
  panelIndex: number,
  update: (panel: EditorPanel) => EditorPanel,
) =>
  updateFullBleed(fullBleeds, fullBleedIndex, fullBleed => ({
    ...fullBleed,
    panels: fullBleed.panels.map((panel, index) =>
      index === panelIndex ? update(panel) : panel,
    ),
  }))

export const updateElement = (
  fullBleeds: EditorFullBleed[],
  fullBleedIndex: number,
  panelIndex: number | undefined,
  elementIndex: number,
  update: (element: EditorElement, panel?: EditorPanel) => EditorElement,
) =>
  typeof panelIndex === 'number'
    ? updatePanel(fullBleeds, fullBleedIndex, panelIndex, panel => ({
        ...panel,
        elements: panel.elements.map((element, index) =>
          index === elementIndex ? update(element, panel) : element,
        ),
      }))
    : updateFullBleed(fullBleeds, fullBleedIndex, fullBleed => ({
        ...fullBleed,
        backgroundElement: fullBleed.backgroundElement
          ? update(fullBleed.backgroundElement)
          : null,
      }))

export const updateElementImage = (
  fullBleeds: EditorFullBleed[],
  fullBleedIndex: number,
  panelIndex: number | undefined,
  elementIndex: number,
  update: (image: EditorImage) => EditorImage,
) =>
  updateElement(
    fullBleeds,
    fullBleedIndex,
    panelIndex,
    elementIndex,
    element => ({
      ...element,
      image: element.image ? update(element.image) : null,
    }),
  )

const newEditorColor = ({ red, green, blue }: ColorFragment): Color => ({
  r: red,
  g: green,
  b: blue,
})

const newEditorElement = (fonts: FontFragment[]) => (
  element: EditorElement,
): Element => ({
  ...element,
  text: element.text && {
    fragments: element.text.map(({ fontId, ...textFragment }) => ({
      ...textFragment,
      color: newEditorColor(textFragment.color),
      fontFamily:
        fonts.find(font => font.id === fontId)?.fontName || 'HelveticaV2',
    })),
  },
})

export const newEditorPanel = (fonts: FontFragment[]) => (
  panel: EditorPanel,
): Panel => ({
  ...panel,
  backgroundColor:
    panel.backgroundColor && newEditorColor(panel.backgroundColor),
  elements: panel.elements.map(newEditorElement(fonts)),
})

export const newEditorCard = (
  card: CardFragment,
  fullBleeds: EditorFullBleed[],
  fonts: FontFragment[],
): Card => ({
  ...card,
  orientation: card.isHorizontal ? 'LANDSCAPE' : 'PORTRAIT',
  fullBleeds: fullBleeds.map(fullBleed => ({
    ...fullBleed,
    backgroundElement:
      fullBleed.backgroundElement &&
      newEditorElement(fonts)(fullBleed.backgroundElement),
    panels: fullBleed.panels.map(newEditorPanel(fonts)),
  })),
})

export const newEditorLayout = (fonts: FontFragment[]) => (
  layout: DetailedLayoutFragment,
): Layout => ({
  ...layout,
  panel: newEditorPanel(fonts)({
    ...layout.panel,
    x: 0,
    y: 0,
    elements: layout.panel.elements.map(EditorElement),
  }),
})

const editorColor = ({ r, g, b }: Color): ColorFragment => ({
  red: r,
  green: g,
  blue: b,
})

const editorElement = (
  element: Element,
  fonts: FontFragment[],
): EditorElement => ({
  ...element,
  image: element.image && {
    ...element.image,
    image: element.image.image && {
      ...element.image.image,
      smallThumb: element.image.image.url,
    },
  },
  text:
    element.text &&
    element.text.fragments.map(({ fontFamily, ...fragment }) => ({
      ...fragment,
      color: editorColor(fragment.color),
      fontId:
        fonts.find(font => font.fontName === fontFamily)?.id ??
        fonts.find(font => font.fontName === 'HelveticaV2')?.id ??
        fonts[0].id,
      textAlign:
        fragment.textAlign === 'LEFT'
          ? TextAlignment.LEFT
          : fragment.textAlign === 'CENTER'
          ? TextAlignment.CENTER
          : fragment.textAlign === 'RIGHT'
          ? TextAlignment.RIGHT
          : undefined,
    })),
})

export const editorPanel = (
  panel: Panel,
  fonts: FontFragment[],
): EditorPanel => ({
  ...panel,
  backgroundColor: panel.backgroundColor && editorColor(panel.backgroundColor),
  elements: panel.elements.map(element => editorElement(element, fonts)),
})

export const editorFullBleed = (fonts: FontFragment[]) => (
  fullBleed: FullBleed,
): EditorFullBleed => ({
  ...fullBleed,
  panels: fullBleed.panels.map(panel => editorPanel(panel, fonts)),
  backgroundElement:
    fullBleed.backgroundElement &&
    editorElement(fullBleed.backgroundElement, fonts),
})
